// src/utils/id-validators.ts

// Country-aware simple validators + MRZ parser (TD3 passports & TD1 IDs)
// Keep it lean; we validate checksum where available.

const MRZ_CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ<";
const WEIGHTS = [7, 3, 1];

function mrzCharValue(ch: string) {
  const i = MRZ_CHARS.indexOf(ch);
  return i >= 0 ? (i <= 9 ? i : (10 + (i - 10))) : 0;
}
function mrzChecksum(data: string) {
  let sum = 0;
  for (let i = 0; i < data.length; i++) {
    sum += mrzCharValue(data[i]) * WEIGHTS[i % 3];
  }
  return String(sum % 10);
}

// TD3 (Passports): 2 lines × 44 chars
const TD3_LINE = /^[A-Z0-9<]{44}$/;
// TD1 (ID cards): 3 lines × 30 chars
const TD1_LINE = /^[A-Z0-9<]{30}$/;

export type MrzResult = {
  raw: string;
  type: "TD3" | "TD1" | "unknown";
  passportNumber?: string;
  passportNumberOK?: boolean;
  nationality?: string;
  birth?: string;     // YYMMDD
  birthOK?: boolean;
  expiry?: string;    // YYMMDD
  expiryOK?: boolean;
  surname?: string;
  givenNames?: string;
};

export function parseMRZ(rawInput: string): MrzResult | null {
  const raw = rawInput.trim().toUpperCase().replace(/[^\nA-Z0-9<]/g, "");
  const lines = raw.split("\n").map(l => l.trim()).filter(Boolean);

  // TD3
  if (lines.length >= 2 && TD3_LINE.test(lines.at(-2)!) && TD3_LINE.test(lines.at(-1)!)) {
    const l1 = lines.at(-2)!; const l2 = lines.at(-1)!;
    // L1: P<ISO<SURNAME<<GIVEN<NAMES
    const parts = l1.split("<<");
    const nameBlock = parts[1] || "";
    const surname = (parts[0].slice(5) || "").replace(/</g, " ").trim(); // P<ISOSURNAME...
    const givenNames = nameBlock.replace(/</g, " ").trim();

    // L2 fields (per ICAO Doc 9303)
    const passportNumber = l2.slice(0, 9);
    const passportNumberCheck = l2.slice(9, 10);
    const nationality = l2.slice(10, 13);
    const birth = l2.slice(13, 19);
    const birthCheck = l2.slice(19, 20);
    const sex = l2.slice(20, 21); // not used
    const expiry = l2.slice(21, 27);
    const expiryCheck = l2.slice(27, 28);

    return {
      raw: `${l1}\n${l2}`,
      type: "TD3",
      passportNumber: passportNumber.replace(/</g, ""),
      passportNumberOK: mrzChecksum(passportNumber) === passportNumberCheck,
      nationality,
      birth,
      birthOK: mrzChecksum(birth) === birthCheck,
      expiry,
      expiryOK: mrzChecksum(expiry) === expiryCheck,
      surname,
      givenNames,
    };
  }

  // TD1 (IDs)
  if (lines.length >= 3 && TD1_LINE.test(lines.at(-3)!) && TD1_LINE.test(lines.at(-2)!) && TD1_LINE.test(lines.at(-1)!)) {
    const l1 = lines.at(-3)!; const l2 = lines.at(-2)!; const l3 = lines.at(-1)!;
    // Simplified parse; main useful fields: doc number, birth, expiry
    const docNumber = l1.slice(5, 14);
    const docNumberCheck = l1.slice(14, 15);
    const birth = l2.slice(0, 6);
    const birthCheck = l2.slice(6, 7);
    const expiry = l2.slice(8, 14);
    const expiryCheck = l2.slice(14, 15);

    // Names often on l3: SURNAME<<GIVEN<NAMES
    const nameBlock = l3.split("<<");
    const surname = (nameBlock[0] || "").replace(/</g, " ").trim();
    const givenNames = (nameBlock[1] || "").replace(/</g, " ").trim();

    return {
      raw: `${l1}\n${l2}\n${l3}`,
      type: "TD1",
      passportNumber: docNumber.replace(/</g, ""),
      passportNumberOK: mrzChecksum(docNumber) === docNumberCheck,
      birth,
      birthOK: mrzChecksum(birth) === birthCheck,
      expiry,
      expiryOK: mrzChecksum(expiry) === expiryCheck,
      surname,
      givenNames,
    };
  }

  return null;
}

// Country-aware passport number patterns (extend as needed)
const COUNTRY_PATTERNS: Record<string, RegExp> = {
  // South Africa: 1 letter + 8 digits
  ZA: /^[A-Z][0-9]{8}$/,
  // Botswana: 2 letters + 7 digits (common), some variations exist
  BW: /^[A-Z]{2}[0-9]{7}$/,
  // Zambia (example pattern; adjust with real spec if needed)
  ZM: /^[A-Z]{2}[0-9]{7,8}$/,
  // Namibia (example)
  NA: /^[A-Z][0-9]{7,8}$/,
  // Zimbabwe (example)
  ZW: /^[A-Z]{2}[0-9]{7}$/,
};

export function isPassportNumber(countryISO2or3: string | undefined, value: string) {
  const v = value.toUpperCase().replace(/\s/g, "");
  const k = (countryISO2or3 || "").toUpperCase();
  const rx = COUNTRY_PATTERNS[k] || /^[A-Z]{1,2}[0-9]{6,9}$/; // general fallback
  return rx.test(v);
}
